import { TriangleAlert } from "lucide-react";
import {
  useCanAccess,
  useNotify,
  useRecordContext,
  useTranslate,
  useUpdate,
} from "ra-core";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

import type { Contact } from "../types";

/**
 * Almikia Epic EC2a: a visible "Revisar nombre" badge (icon plus text) while the surname split
 * of the contact is uncertain (`name_needs_review`), with a one-tap "Marcar como revisado" that
 * clears the flag. The action is offered only to the contact's editors; the DB stays the authority.
 */
export const ContactNameReview = () => {
  const record = useRecordContext<Contact>();
  const translate = useTranslate();
  const notify = useNotify();
  const [update, { isPending }] = useUpdate<Contact>();
  const { canAccess: canWrite, isPending: isWritePending } = useCanAccess({
    resource: "contacts",
    action: "edit",
    record,
  });
  if (!record?.name_needs_review) return null;

  const markReviewed = () =>
    update(
      "contacts",
      {
        id: record.id,
        data: { name_needs_review: false },
        previousData: record,
      },
      {
        onError: (error) =>
          notify(
            error instanceof Error
              ? error.message
              : "ra.notification.http_error",
            { type: "error" },
          ),
      },
    );

  return (
    <div className="flex flex-wrap items-center gap-2 mt-1">
      <Badge
        variant="outline"
        className="gap-1 text-sm text-brand-red-text bg-brand-red-bg border-transparent"
      >
        <TriangleAlert className="size-4" aria-hidden="true" />
        {translate("resources.contacts.name_review.badge")}
      </Badge>
      {!isWritePending && canWrite && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="min-h-11 md:min-h-8 text-base md:text-sm"
          disabled={isPending}
          onClick={markReviewed}
        >
          {translate("resources.contacts.name_review.mark_reviewed")}
        </Button>
      )}
    </div>
  );
};
